import { useState, useEffect, useCallback } from "react";
import type { Email, OrganizationCustomerRow } from "@src/types/api";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";

export type AllEmailsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ok";
      emails: Email[];
      organizationCustomers: OrganizationCustomerRow[];
      internalUsers: string[];
    };

export function useAllEmailsData(refreshToken = 0): {
  state: AllEmailsState;
  refresh: () => void;
} {
  const { loadApp, office } = useTaskpaneServices();
  const [state, setState] = useState<AllEmailsState>({ status: "loading" });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      setState({ status: "loading" });
      await office.onReady();
      if (cancelled) return;

      // Collect the same thread identifiers the single-email view uses, so the dashboard can
      // resolve the org anchor from conversationId or thread headers when the open item's
      // internetMessageId is missing or not yet persisted.
      const meta = office.getItemMeta();
      const fromHeaders = await office.getThreadMessageIdCandidatesFromHeaders();
      const originalFromHeaders = await office.getOriginalInternetMessageIdFromHeaders();

      if (cancelled) return;
      let primary = meta?.internetMessageId?.trim() ?? "";

      if (!primary) primary = (originalFromHeaders ?? "").trim();
      if (!primary && fromHeaders.length > 0) primary = fromHeaders[0].trim();
      const conversationId = meta?.conversationId?.trim() || null;
      const seen = new Set<string>();
      const candidates: string[] = [];

      for (const id of [...fromHeaders, originalFromHeaders ?? ""]) {
        const trimmed = typeof id === "string" ? id.trim() : "";

        if (trimmed && trimmed !== primary && !seen.has(trimmed)) {
          seen.add(trimmed);
          candidates.push(trimmed);
        }
      }

      if (!primary && !conversationId && candidates.length === 0) {
        setState({ status: "error", message: "No email selected — open an email first." });
        return;
      }

      try {
        const res = await loadApp.getAllEmails(
          primary || undefined,
          controller.signal,
          conversationId,
          candidates.length > 0 ? candidates : undefined
        );

        if (!cancelled) {
          setState({
            status: "ok",
            emails: res?.emails ?? [],
            organizationCustomers: res?.organizationCustomers ?? [],
            internalUsers: res?.internalUsers ?? [],
          });
        }
      } catch (err: unknown) {
        if (!cancelled && !controller.signal.aborted) {
          const msg = err instanceof Error ? err.message : "Failed to load shipments";

          setState({ status: "error", message: msg });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [loadApp, office, version, refreshToken]);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  // Reload the org-wide dashboard when the user switches Outlook items.
  useEffect(() => {
    const subscribe = office.onItemChanged;

    if (typeof subscribe !== "function") return;
    const unsubscribe = subscribe(() => setVersion((v) => v + 1));

    return () => {
      unsubscribe?.();
    };
  }, [office]);

  return { state, refresh };
}
